<template>
  <div class="min-h-app-screen bg-gray-50 dark:bg-[#0d1117]">
    <FlashNotifications ref="flash" />

    <!-- Header skeleton -->
    <div v-if="eventLoading" class="bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700 px-4 sm:px-6 py-5 animate-pulse">
      <div class="flex items-center gap-3">
        <div class="w-9 h-9 rounded-full bg-gray-200 dark:bg-gray-700"></div>
        <div class="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-700"></div>
        <div class="h-4 w-40 bg-gray-200 dark:bg-gray-700 rounded"></div>
      </div>
      <div class="flex gap-6 mt-6">
        <div v-for="n in 6" :key="n" class="h-3 w-16 bg-gray-100 dark:bg-gray-800 rounded"></div>
      </div>
    </div>

    <!-- Event load error -->
    <div v-else-if="eventError" class="flex flex-col items-center justify-center py-32 text-center px-4">
      <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
        <svg class="w-7 h-7 text-red-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/>
        </svg>
      </div>
      <h3 class="text-base font-semibold text-gray-900 dark:text-white">Something went wrong</h3>
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ eventError }}</p>
      <button @click="loadEvent" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
        Try again
      </button>
    </div>

    <template v-else-if="event">
      <!-- The nav lives here, not in each tab, so switching tabs keeps it
           mounted and the event is fetched once rather than per tab. -->
      <EventSubNav :event="event" :current-tab="currentTab" @updated="applyEventPatch" />

      <router-view v-slot="{ Component }">
        <component :is="Component" :event="event" />
      </router-view>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, watch, provide, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useRequest } from 'composables/useRequest.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import EventSubNav from 'controllers/vue_components/events/EventSubNav'

const route = useRoute()
const flash = ref(null)
const { axiosRequest } = useRequest(flash)

// Header actions toast through this layout-level flash, which stays mounted
// while the header itself is swapped for the skeleton during a reload.
provide('eventFlash', flash)

const event        = ref(null)
const eventLoading = ref(true)
const eventError   = ref(null)

const currentTab = computed(() => route.meta.eventTab || 'bookings')

// Header actions (privacy, active) report what changed; applying it here keeps
// the header and the active tab on the same event object.
function applyEventPatch(patch) {
  if (event.value) Object.assign(event.value, patch)
}

async function loadEvent() {
  eventLoading.value = true
  eventError.value   = null
  const res = await axiosRequest('GET', `/api/v1/events/${route.params.id}`, {}, false)
  if (!res.js_error && res.response.success) {
    event.value = res.response.data
  } else {
    eventError.value = res.response?.message || 'Failed to load event.'
  }
  eventLoading.value = false
}

// Tab switches keep the same id and reuse this instance, so only a move to a
// different event triggers a refetch.
watch(() => route.params.id, (id, prev) => {
  if (id && id !== prev) {
    event.value = null
    loadEvent()
  }
})

onMounted(loadEvent)
</script>
